import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const Container = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  margin-bottom: 30px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin: 0;
  color: #fff;
`;

const HideButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 15px;
  color: #ccc;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
`;

const Input = styled.input`
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #fff;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: #888;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.danger ? '#c33' : '#4CAF50'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.danger ? '#a22' : '#45a049'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UserCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Username = styled.div`
  font-weight: 600;
  color: #fff;
`;

const Email = styled.div`
  font-size: 0.85rem;
  color: #999;
`;

const Role = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  border-radius: 3px;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-left: 10px;
`;

const Message = styled.div`
  padding: 12px;
  margin-bottom: 15px;
  border-radius: 4px;
  background: ${props => props.error ? 'rgba(204, 51, 51, 0.2)' : 'rgba(76, 175, 80, 0.2)'};
  color: ${props => props.error ? '#ff6b6b' : '#4CAF50'};
  border: 1px solid ${props => props.error ? 'rgba(204, 51, 51, 0.3)' : 'rgba(76, 175, 80, 0.3)'};
`;

function UserManagement({ onClose }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/users`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setUsers(data.users);
      } else {
        setMessage({ error: true, text: data.error || 'Failed to load users' });
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ error: true, text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ error: false, text: `User "${data.user.username}" created successfully!` });
        setNewUser({ username: '', email: '', password: '' });
        loadUsers(); // Reload users list
      } else {
        setMessage({ error: true, text: data.error || 'Failed to create user' });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setMessage({ error: true, text: 'Failed to create user' });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/users/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ error: false, text: `User "${username}" deleted successfully!` });
        loadUsers(); // Reload users list
      } else {
        setMessage({ error: true, text: data.error || 'Failed to delete user' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({ error: true, text: 'Failed to delete user' });
    }
  };

  if (loading) {
    return (
      <Container>
        <TitleRow>
          <Title>User Management</Title>
          <HideButton onClick={onClose}>Hide User Management</HideButton>
        </TitleRow>
        <div style={{ color: '#999' }}>Loading users...</div>
      </Container>
    );
  }

  return (
    <Container>
      <TitleRow>
        <Title>User Management</Title>
        <HideButton onClick={onClose}>Hide User Management</HideButton>
      </TitleRow>

      {message && (
        <Message error={message.error}>
          {message.text}
        </Message>
      )}

      <Section>
        <SectionTitle>Create New User</SectionTitle>
        <Form onSubmit={handleCreateUser}>
          <Input
            type="text"
            placeholder="Username"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            required
            disabled={creating}
          />
          <Input
            type="email"
            placeholder="Email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            required
            disabled={creating}
          />
          <Input
            type="password"
            placeholder="Password (min 6 characters)"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            required
            minLength={6}
            disabled={creating}
          />
          <Button type="submit" disabled={creating}>
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </Form>
      </Section>

      <Section>
        <SectionTitle>Existing Users ({users.length})</SectionTitle>
        <UsersList>
          {users.map(user => (
            <UserCard key={user.username}>
              <UserInfo>
                <Username>
                  {user.username}
                  <Role>{user.role}</Role>
                  {currentUser?.username === user.username && (
                    <span style={{ color: '#888', marginLeft: '10px', fontSize: '0.85rem' }}>(You)</span>
                  )}
                </Username>
                <Email>{user.email}</Email>
              </UserInfo>
              <Button
                danger
                onClick={() => handleDeleteUser(user.username)}
                disabled={currentUser?.username === user.username}
                title={currentUser?.username === user.username ? "Cannot delete your own account" : "Delete user"}
              >
                Delete
              </Button>
            </UserCard>
          ))}
        </UsersList>
      </Section>
    </Container>
  );
}

export default UserManagement;
